import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import { When } from 'react-if';
import { Alert, AlertTitle, Button, IconButton } from '@mui/material';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { MessagePayload } from 'firebase/messaging';

import { ALERT_URLS, NO_SOUND } from '../../constants/sounds.constants';
import { RootState } from '../../store';
import { AlertService } from '../../services/alert.service';
import { AlertDetailsDrawer } from '../details-drawers/alert-details-drawer';
import { AlertTypes } from '../../types';
import useAudio from '../../hooks/useAudio';
import { useGenericQuery } from '../../services/react-query/generic.query';

export function AlertListener() {
    const { data, refetch } = useGenericQuery<{ viewed: number, unviewed: number, unviewedAlerts: AlertTypes.Model[] }>(
        'fetchUnviewedAlerts',
        'alerts/count',
        2500
    );
    const soundUrl =
        useSelector(
            (state: RootState) => state?.customer?.details?.customer?.settings?.alertSoundUrl
        ) || ALERT_URLS[0].soundUrl;

    const { play, stop, muted, toggleMute } = useAudio(soundUrl);

    const [recallMessageListening, setRecallMessageListening] = useState<MessagePayload | undefined>();
    const [alertDetailModalOpen, setAlertDetailModalOpen] = useState(false);
    const [viewedAlert, setViewedAlert] = useState({} as AlertTypes.Model);
    const viewedCount = data || { viewed: 0, unviewed: 0, unviewedAlerts: [] };

    const [firstUnseenAlert, setFirstUnseenAlert] = useState(
        _.first(viewedCount.unviewedAlerts)!
    );

    const localeTimeOfAlert = new Date(
        firstUnseenAlert?.initiatedAt
    ).toLocaleDateString();

    useEffect(() => {
        if (viewedCount?.unviewed > 0 && soundUrl !== NO_SOUND) {
            play();
        } else {
            stop();
        }
    }, [viewedCount?.unviewed, soundUrl]);

    useEffect(() => {
        setFirstUnseenAlert(_.first(viewedCount.unviewedAlerts)!);
    }, [viewedCount.unviewedAlerts]);

    return (
        <>
            <When condition={viewedCount.unviewed > 0}>
                <Alert
                    sx={{
                        margin: 1,
                        padding: 1,
                        position: 'fixed',
                        top: '0%',
                        left: '27%',
                        zIndex: '1599',
                        wordBreak: 'break-word',
                        width: '51%',
                    }}
                    severity="info"
                    onClick={async () => {
                        await AlertService.view(firstUnseenAlert?.id);
                        setViewedAlert(firstUnseenAlert);
                        setAlertDetailModalOpen(true);
                        await refetch();
                        stop();
                    }}
                    action={
                        <>
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleMute();
                                }}
                                size="small"
                                sx={{ mr: 1 }}
                            >
                                {muted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                            </IconButton>
                            <Button>VIEW</Button>
                        </>
                    }
                >
                    <AlertTitle>
                        {viewedCount.unviewed > 1
                            ? `You have ${viewedCount.unviewed} unviewed, active alerts  `
                            : `You have one (1) unviewed, active alert  `}
                        from {firstUnseenAlert?.user?.firstName} {'  '}, Date:{' '}
                        {localeTimeOfAlert}, System:
                        {'  '}
                        {firstUnseenAlert?.initiatedBy}
                        {'  '}
                    </AlertTitle>
                </Alert>
            </When>
            {(alertDetailModalOpen && viewedAlert) &&
                <AlertDetailsDrawer
                    onClose={async () => {
                        setAlertDetailModalOpen(false);
                        // Refetch to check if there are still unviewed alerts
                        const updatedData = await refetch();
                        // If no more unviewed alerts, stop the sound
                        if (updatedData?.data?.unviewed === 0) {
                            stop();
                        }
                    }}
                    visible={alertDetailModalOpen}
                    alert={viewedAlert}
                    type={viewedAlert?.alertType}
                />
            }

        </>
    );
}
